// src/services/gameGeneratorEmoji.js
//
// Word -> emoji for the game generator's icons, plus word banks used for
// wrong answers when a song is too short to supply enough of its own.
//
// Lowercase, singular keys. emojiFor() also tries the singular of a plural
// ("planets" -> "planet"). Add words freely; nothing else needs changing.

export const EMOJI = {
  // ---- science: earth, space, weather ----
  sun: "☀️", moon: "🌙", star: "⭐", planet: "🪐", earth: "🌍", world: "🌎",
  sky: "🌤️", space: "🚀", rocket: "🚀", astronaut: "👩‍🚀", comet: "☄️",
  orbit: "🪐", galaxy: "🌌", telescope: "🔭", mars: "🔴", saturn: "🪐",
  cloud: "☁️", rain: "🌧️", snow: "❄️", storm: "⛈️", thunder: "⛈️",
  lightning: "⚡", wind: "🌬️", tornado: "🌪️", hurricane: "🌀", rainbow: "🌈",
  weather: "🌦️", temperature: "🌡️", thermometer: "🌡️", hot: "🔥", cold: "🥶",
  ice: "🧊", water: "💧", drop: "💧", ocean: "🌊", sea: "🌊", wave: "🌊",
  river: "🏞️", lake: "🏞️", mountain: "⛰️", volcano: "🌋", rock: "🪨",
  stone: "🪨", soil: "🟫", sand: "🏖️", desert: "🏜️", island: "🏝️",
  forest: "🌲", fossil: "🦴", crystal: "💎", mineral: "💎", magnet: "🧲",
  evaporation: "♨️", condensation: "☁️", precipitation: "🌧️", collection: "🪣",
  season: "🍂", spring: "🌷", summer: "😎", fall: "🍂", autumn: "🍁",
  winter: "⛄", day: "🌞", night: "🌙", shadow: "👤", light: "💡",
  energy: "⚡", heat: "🔥", fire: "🔥", electricity: "🔌", battery: "🔋",
  sound: "🔊", music: "🎵", song: "🎵", vibration: "〰️", motion: "🏃",
  force: "💪", push: "👉", pull: "🫳", gravity: "🍎", speed: "💨",
  friction: "🧽", position: "📍", object: "📦", matter: "🧪", solid: "🧱",
  liquid: "💧", gas: "💨", air: "🌬️", oxygen: "🫁", experiment: "🧪",
  scientist: "🧑‍🔬", science: "🔬", microscope: "🔬", atom: "⚛️",
  // ---- science: living things ----
  plant: "🌱", seed: "🌱", flower: "🌸", tree: "🌳", leaf: "🍃", root: "🌱",
  stem: "🌿", fruit: "🍎", apple: "🍎", vegetable: "🥕", carrot: "🥕",
  corn: "🌽", grass: "🌾", animal: "🐾", habitat: "🏡", dog: "🐶", cat: "🐱",
  bird: "🐦", fish: "🐟", frog: "🐸", turtle: "🐢", snake: "🐍", lizard: "🦎",
  bee: "🐝", butterfly: "🦋", caterpillar: "🐛", insect: "🐞", bug: "🐞",
  spider: "🕷️", ant: "🐜", worm: "🪱", lion: "🦁", tiger: "🐯", bear: "🐻",
  elephant: "🐘", monkey: "🐒", horse: "🐴", cow: "🐄", pig: "🐷",
  chicken: "🐔", egg: "🥚", duck: "🦆", owl: "🦉", whale: "🐋", shark: "🦈",
  dolphin: "🐬", octopus: "🐙", crab: "🦀", penguin: "🐧", rabbit: "🐰",
  mouse: "🐭", deer: "🦌", wolf: "🐺", fox: "🦊", dinosaur: "🦕",
  mammal: "🐾", reptile: "🦎", amphibian: "🐸", life: "🌱", body: "🧍",
  heart: "❤️", brain: "🧠", bone: "🦴", skeleton: "💀", muscle: "💪",
  lung: "🫁", tooth: "🦷", teeth: "🦷", eye: "👁️", ear: "👂", nose: "👃",
  hand: "✋", food: "🍎", eat: "🍽️", germ: "🦠", healthy: "🥦",
  // ---- math ----
  number: "🔢", count: "🔢", add: "➕", addition: "➕", plus: "➕",
  subtract: "➖", subtraction: "➖", minus: "➖", multiply: "✖️",
  multiplication: "✖️", times: "✖️", divide: "➗", division: "➗",
  equal: "🟰", equals: "🟰", fraction: "🍕", half: "🌗", pizza: "🍕",
  shape: "🔷", circle: "⭕", square: "🟥", triangle: "🔺", rectangle: "▭",
  money: "💰", coin: "🪙", penny: "🪙", dollar: "💵", clock: "🕒",
  time: "⏰", hour: "⏰", minute: "⏱️", ruler: "📏", measure: "📏",
  inch: "📏", foot: "🦶", graph: "📊", pattern: "🔁", zero: "0️⃣",
  one: "1️⃣", two: "2️⃣", three: "3️⃣", four: "4️⃣", five: "5️⃣", six: "6️⃣",
  seven: "7️⃣", eight: "8️⃣", nine: "9️⃣", ten: "🔟", hundred: "💯",
  // ---- social studies ----
  flag: "🇺🇸", country: "🗺️", map: "🗺️", globe: "🌐",
  compass: "🧭", north: "⬆️", south: "⬇️", east: "➡️", west: "⬅️",
  continent: "🌍", city: "🏙️", town: "🏘️", community: "🏘️", home: "🏠",
  house: "🏠", school: "🏫", teacher: "🧑‍🏫", student: "🧑‍🎓", family: "👪",
  friend: "🤝", farm: "🚜", farmer: "🧑‍🌾", job: "🧰", worker: "👷",
  bank: "🏦", store: "🏪", market: "🛒", goods: "📦",
  service: "🛎️", trade: "🤝", law: "⚖️", rule: "📜", vote: "🗳️",
  president: "🏛️", government: "🏛️", capital: "🏛️", leader: "🎖️",
  citizen: "🧑", freedom: "🗽", liberty: "🗽", history: "📜",
  future: "🔮", holiday: "🎉", ship: "🚢", boat: "⛵", train: "🚂",
  car: "🚗", bus: "🚌", truck: "🚚", plane: "✈️", bike: "🚲", road: "🛣️",
  bridge: "🌉", king: "👑", queen: "👑", crown: "👑", gold: "🥇",
  native: "🪶", tribe: "🪶", explorer: "🧭", colony: "🏘️", war: "⚔️",
  peace: "🕊️", tool: "🔧", hammer: "🔨", book: "📚", read: "📖",
  write: "✏️", pencil: "✏️", letter: "✉️", word: "🔤", alphabet: "🔤",
  // ---- music & feelings ----
  guitar: "🎸", violin: "🎻", flute: "🪈", drum: "🥁", trumpet: "🎺",
  piano: "🎹", sing: "🎤", dance: "💃", clap: "👏", happy: "😊", sad: "😢",
  love: "❤️", fun: "🎉", play: "⚽", game: "🎮", ball: "⚽", jump: "🦘",
  run: "🏃", walk: "🚶", swim: "🏊", sleep: "😴", smile: "😊",
};

export const emojiFor = (word) => {
  const w = String(word || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return "";
  if (EMOJI[w]) return EMOJI[w];
  const tries = [];
  if (w.endsWith("ies")) tries.push(`${w.slice(0, -3)}y`);
  if (w.endsWith("es")) tries.push(w.slice(0, -2));
  if (w.endsWith("s")) tries.push(w.slice(0, -1));
  if (w.endsWith("ing")) tries.push(w.slice(0, -3), `${w.slice(0, -3)}e`);
  if (w.endsWith("ed")) tries.push(w.slice(0, -2), w.slice(0, -1));
  const hit = tries.find((t) => EMOJI[t]);
  return hit ? EMOJI[hit] : "";
};

/**
 * An emoji as an image the games can show. The games' image fields go
 * through utils/resolveImageSrc, which passes a data: URL straight through,
 * so this needs no upload and no Storage file.
 */
export const emojiImage = (emoji) => {
  if (!emoji) return "";
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">` +
    `<text x="50" y="56" font-size="78" text-anchor="middle" dominant-baseline="middle">${emoji}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

/** Reverse of emojiImage, for the editor's icon box. */
export const emojiFromImage = (src) => {
  const s = String(src || "");
  if (!s.startsWith("data:image/svg+xml")) return "";
  try {
    const svg = decodeURIComponent(s.split(",").slice(1).join(","));
    const m = svg.match(/<text[^>]*>([^<]*)<\/text>/);
    return m ? m[1] : "";
  } catch {
    return "";
  }
};

// Wrong-answer banks, by subject. Used only when the song itself doesn't
// supply enough believable wrong answers, and for "Which word is in the
// song?" / sorting games, where wrong answers must NOT be in the lyrics.
export const WORD_BANKS = {
  science: [
    "magnet", "shadow", "volcano", "planet", "fossil", "gravity", "insect",
    "habitat", "thunder", "crystal", "battery", "skeleton", "oxygen",
    "rainbow", "seasons", "friction", "mineral", "ocean", "desert", "energy",
    "liquid", "orbit", "telescope", "caterpillar", "temperature", "soil",
    "vibration", "electricity", "weather", "lightning",
  ],
  math: [
    "fraction", "triangle", "rectangle", "hundred", "addition", "subtract",
    "multiply", "divide", "equal", "pattern", "measure", "minute", "dollar",
    "penny", "circle", "square", "graph", "ruler", "number", "double",
    "half", "dozen", "inch", "estimate", "sum", "difference",
  ],
  socialstudies: [
    "community", "president", "citizen", "compass", "continent", "government",
    "explorer", "colony", "capital", "liberty", "freedom", "history", "map",
    "goods", "services", "trade", "farmer", "vote", "law", "river",
    "mountain", "globe", "holiday", "leader", "tribe", "bridge",
  ],
  other: [
    "rainbow", "music", "friend", "garden", "rocket", "pencil", "alphabet",
    "family", "school", "picture", "story", "teacher", "planet", "ocean",
    "happy", "dance", "number", "color", "circle", "butterfly",
  ],
};

export const DEFAULT_TILES = ["⭐", "🎵", "📚", "🍎", "🌈", "🚀"];
